import React, { FunctionComponent, useRef, useState } from 'react';
import { Box, Divider, Grid, IconButton } from '@mui/material';
import CustomPagination from '../CustomPagination';
import DebouncedLoader from '../DebouncedLoader';
import NoResultsOverlay from './CustomNoResultsOverlay';
import ResultHeader from '../../resourcesPage/ResourcesResultHeader';
import { usePrefilteredResourceLibraryItems } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import ResourcesCard from './ResourcesCard';
import { useResourceTypes } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import { useBrands } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/BrandsProvider';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import PreFilteredLibraryDrawer from '../../automotivePage/PrefilteredLibraryDrawer';
import FilterControllerIcon from '../../productPage/filterComponents/FilterControllerIcon';

interface ResourcesListProps {
    view?: string;
    backgroundColor?: string;
    isTechnicalResources?: boolean;
}

const PreFilteredResourcesList: FunctionComponent<ResourcesListProps> = ({
    view,
    backgroundColor,
    isTechnicalResources,
}) => {
    const {
        filteredItems,
        paginatedItems,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        sortOrder,
        setSortOrder,
        resetFilters,
        loading: resourceLibraryItemsLoading,
    } = usePrefilteredResourceLibraryItems();

    const { loading: typesLoading } = useResourceTypes();
    const { loading: brandsLoading } = useBrands();
    const { isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const isLoading = resourceLibraryItemsLoading || typesLoading || brandsLoading;
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;
    const [headerView, setHeaderView] = React.useState('module');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const topRef = useRef<HTMLDivElement | null>(null);

    // No preprocessing needed
    const documents = paginatedItems;

    const handlePageChange = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setCurrentPage(newPage + 1);
    };

    const showThumbnails = isTechnicalResources ? view === 'module' : headerView === 'module';

    return (
        <>
            {!isTechnicalResources && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        {isSmallScreen && (
                            <IconButton
                                onClick={() => setDrawerOpen(true)}
                                sx={{ marginLeft: '1rem' }}
                            >
                                <FilterControllerIcon />
                            </IconButton>
                        )}
                        <ResultHeader
                            onReset={resetFilters}
                            isDataSheet={false}
                            items={filteredItems}
                            sort={sortOrder}
                            setSort={setSortOrder}
                            view={headerView}
                            setView={setHeaderView}
                        />
                    </Box>
                    <Divider
                        orientation="horizontal"
                        sx={{ width: '100%', border: '0.1px solid #E5673E' }}
                    />
                </>
            )}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Box sx={{ width: isSmallScreen ? 0 : '22%' }}>
                    <PreFilteredLibraryDrawer
                        openDrawer={drawerOpen}
                        setOpenDrawer={setDrawerOpen}
                    />
                </Box>

                <Box
                    sx={{
                        overflow: 'hidden',
                        width: isSmallScreen ? '100%' : '80%',
                    }}
                >
                    {documents.length === 0 && !isLoading ? (
                        <Box
                            sx={
                                isTechnicalResources
                                    ? {
                                          marginLeft: '30rem',
                                          marginTop: '2.5rem',
                                          marginBottom: '1.5rem',
                                          color: '#B75332',
                                      }
                                    : {
                                          width: '100%',
                                          margin: '8rem 0',
                                          color: '#B75332',
                                      }
                            }
                        >
                            <NoResultsOverlay />
                        </Box>
                    ) : (
                        <>
                            <DebouncedLoader
                                loading={isLoading}
                                delay={0}
                                size={40}
                                sx={{ margin: '8rem 0', color: '#B75332' }}
                            >
                                <div ref={topRef} data-scroll-anchor />
                                <Box sx={{ marginTop: '1rem' }}>
                                    {documents.length > 0 && (
                                        <CustomPagination
                                            count={filteredItems.length}
                                            page={currentPage - 1}
                                            onPageChange={handlePageChange}
                                            rowsPerPage={itemsPerPage}
                                            // scrollAnchorRef={topRef}
                                            // autoScrollTop
                                            // loading={isLoading}
                                            // scrollOffset={99}
                                        />
                                    )}
                                </Box>
                                <Grid container spacing={2} direction="column" display="flex">
                                    {documents.map((doc, index) => (
                                        <Grid item xs={12} key={doc.id} marginLeft="2rem">
                                            <ResourcesCard
                                                item={doc}
                                                backgroundColor={backgroundColor}
                                                showThumbnail={showThumbnails}
                                                clickableHeader={true}
                                                displayAssetLinks={true}
                                                hideLanguageVariantsCondition={'never'}
                                                hideViewAssetLabelCondition={'always'}
                                            />
                                            {index < documents.length - 1 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Divider sx={{ width: '90%' }} />
                                                </Box>
                                            )}
                                        </Grid>
                                    ))}
                                </Grid>
                                <Box sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                    {documents.length > 0 && (
                                        <CustomPagination
                                            count={filteredItems.length}
                                            page={currentPage - 1}
                                            onPageChange={handlePageChange}
                                            rowsPerPage={itemsPerPage}
                                            scrollAnchorRef={topRef}
                                            autoScrollTop
                                            loading={isLoading}
                                            scrollOffset={99}
                                        />
                                    )}
                                </Box>
                            </DebouncedLoader>
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default PreFilteredResourcesList;
